import type { UserRole } from '@omnea-app/packages/universal/users/userRole'

/** The user fields the organisation-role mapping reads. */
export type UserRoleTierConfig = {
  role: UserRole
  hasConfigPermissions: boolean
  isViewOnly: boolean
}

/**
 * The organisation relations the sync pipeline derives from users.role — one per legacy role
 * tier. The `derived_` prefix marks them recomputable/sync-owned (never granted via the API).
 * `derived_owner` (super admins) carries full configuration access; the others carry no
 * permissions yet and exist so future relations can consume them.
 */
export const DERIVED_ORGANISATION_ROLES = ['derived_owner', 'derived_admin', 'derived_viewer', 'derived_user'] as const

/** An organisation role relation derived from users.role. */
export type DerivedOrganisationRole = (typeof DERIVED_ORGANISATION_ROLES)[number]

/**
 * Maps a user's role tier to their derived organisation relation. These are our durable authz
 * identifiers, deliberately decoupled from the product's user-facing role labels so a label
 * rename can't silently rewrite persisted tuples. Suppliers hold no organisation role.
 */
export const mapUserToOrganisationRole = (user: UserRoleTierConfig): DerivedOrganisationRole | null => {
  switch (user.role) {
    case 'supplier':
      return null
    case 'user':
      return 'derived_user'
    case 'editor':
      if (user.isViewOnly) return 'derived_viewer'
      if (user.hasConfigPermissions) return 'derived_owner'
      return 'derived_admin'
  }
}
